import type { Item, MembreId } from './item';
import { jourDeEcheance, jourLocal, libelleDate } from './temps';

/**
 * Le brief quotidien. Construit par le meme code des deux cotes : le Worker
 * s'en sert a 6 h 30 pour rediger la notification, le front pour peindre
 * l'ecran d'accueil et le tenir a jour en temps reel.
 *
 * Chaque membre recoit le meme contenu ; c'est l'affichage qui remonte en
 * premier ce qui lui est assigne.
 */
export interface Brief {
  famille_id: string;
  /** AAAA-MM-JJ dans le fuseau de la famille. */
  date_locale: string;
  /** Deja formate en francais, ex. « vendredi 19 septembre ». */
  libelle_date: string;
  genere_le: string;
  rdv_du_jour: Item[];
  taches_du_jour: Item[];
  taches_en_retard: Item[];
}

export interface MembrePublic {
  id: MembreId;
  prenom: string;
  couleur: string;
  /**
   * Un enfant est une personne dont on s'occupe, pas une personne a qui l'on
   * confie une tache : il n'apparait jamais dans le choix « Pour qui », n'a
   * pas de PIN et ne recoit aucune notification.
   */
  role: 'adulte' | 'enfant';
}

export function estAdulte(m: MembrePublic): boolean {
  return m.role !== 'enfant';
}

export function estEnfant(m: MembrePublic): boolean {
  return m.role === 'enfant';
}

export interface GroupesBrief {
  rdv_du_jour: Item[];
  taches_du_jour: Item[];
  taches_en_retard: Item[];
}

/**
 * Repartit les items dans les trois sections du brief.
 *
 * Le tri se fait ici et non en SQL : SQLite ne sait pas projeter un instant
 * vers America/Toronto, et coder un decalage en dur marcherait sept mois par
 * an — le genre de bug qui se revele un dimanche de novembre.
 */
export function grouperBrief(items: Item[], date_locale: string, fuseau: string): GroupesBrief {
  const rdv_du_jour: Item[] = [];
  const taches_du_jour: Item[] = [];
  const taches_en_retard: Item[] = [];

  for (const item of items) {
    if (!item.du_le || item.statut === 'annule') continue;
    const jour = jourDeEcheance(item.du_le, fuseau);
    if (!jour) continue;

    if (item.type === 'rdv') {
      if (jour === date_locale) rdv_du_jour.push(item);
      continue;
    }
    if (item.type === 'tache' && item.statut === 'todo') {
      if (jour === date_locale) taches_du_jour.push(item);
      else if (jour < date_locale) taches_en_retard.push(item);
    }
  }

  const parEcheance = (a: Item, b: Item) => (a.du_le ?? '').localeCompare(b.du_le ?? '');
  rdv_du_jour.sort(parEcheance);
  taches_du_jour.sort(parEcheance);
  // Les plus anciennes d'abord : ce sont celles qui trainent depuis le plus
  // longtemps, donc celles qu'il faut voir.
  taches_en_retard.sort(parEcheance);

  return { rdv_du_jour, taches_du_jour, taches_en_retard };
}

export function construireBriefDepuis(
  items: Item[],
  famille_id: string,
  fuseau: string,
  instant = new Date(),
): Brief {
  const date_locale = jourLocal(instant, fuseau);
  return {
    famille_id,
    date_locale,
    libelle_date: libelleDate(instant, fuseau),
    genere_le: instant.toISOString(),
    ...grouperBrief(items, date_locale, fuseau),
  };
}

export function briefEstVide(b: Brief): boolean {
  return (
    b.rdv_du_jour.length === 0 &&
    b.taches_du_jour.length === 0 &&
    b.taches_en_retard.length === 0
  );
}

/**
 * Remonte en tete ce qui est assigne au membre donne, sans casser l'ordre
 * chronologique a l'interieur de chaque groupe. Un tri stable suffit : ce qui
 * m'est assigne d'abord, le reste ensuite, chacun toujours par echeance.
 */
export function mesDabord(items: Item[], membre_id: string): Item[] {
  const miens = items.filter((i) => i.assigne_a === membre_id);
  const autres = items.filter((i) => i.assigne_a !== membre_id);
  return [...miens, ...autres];
}

/** Charge utile de la notification push. Volontairement minuscule. */
export interface PayloadPush {
  titre: string;
  corps: string;
  /** Chemin a ouvrir au clic. */
  url: string;
  /**
   * Regroupement dans le centre de notifications : deux notifications de meme
   * tag se remplacent. Le brief du jour porte `brief-<date>`, de sorte qu'un
   * brief non lu soit remplace par celui du lendemain — mais une assignation
   * de tache porte son propre tag, sinon elle effacerait le brief.
   */
  tag: string;
  /** Jour du brief, quand la notification en est un. */
  jour?: string;
}
